import { useQuery } from "@tanstack/react-query";
import axios from "axios";

// =============================================================================
// Types
// =============================================================================

interface MorningstarTokenRequest {
  appId: string;
  apiKey: string;
}

interface MorningstarTokenResponse {
  success: boolean;
  data: {
    token: string;
    expiresIn: number; // seconds
    appId: string;
  };
}

// =============================================================================
// API Function
// =============================================================================

export const fetchMorningstarToken = async (): Promise<string> => {
  const baseUrl = process.env.REACT_APP_MORNINGSTAR_API_BASE_URL;
  const appId = process.env.REACT_APP_MORNINGSTAR_API_APP_ID;
  const apiKey = process.env.REACT_APP_MORNINGSTAR_API_KEY;

  if (!baseUrl) {
    throw new Error("REACT_APP_MORNINGSTAR_API_BASE_URL is not defined");
  }
  if (!appId) {
    throw new Error("REACT_APP_MORNINGSTAR_API_APP_ID is not defined");
  }
  if (!apiKey) {
    throw new Error("REACT_APP_MORNINGSTAR_API_KEY is not defined");
  }

  const response = await axios.post<MorningstarTokenResponse>(
    `${baseUrl}/api/auth/token`,
    {
      appId,
      apiKey,
    } as MorningstarTokenRequest,
    {
      timeout: 15000,
      headers: {
        "Content-Type": "application/json",
      },
    }
  );

  // 204 No Content means the server is warming up (cold start) — treat as retryable
  if (response.status === 204 || !response.data) {
    throw new Error("Morningstar API is warming up, retrying...");
  }

  if (!response.data.success || !response.data.data?.token) {
    throw new Error("Failed to get Morningstar token");
  }

  return response.data.data.token;
};

// =============================================================================
// Hook
// =============================================================================

/**
 * Hook to get and manage Morningstar API authentication token.
 * Token is cached and automatically refreshed when it expires (24 hours).
 */
export const useMorningstarToken = () => {
  const query = useQuery({
    queryKey: ["morningstar", "token"],
    queryFn: fetchMorningstarToken,
    staleTime: 23 * 60 * 60 * 1000, // 23 hours (refresh before 24h expiry)
    cacheTime: 24 * 60 * 60 * 1000, // 24 hours
    retry: 6,
    // Exponential backoff: 2s, 4s, 8s, 16s, 30s, 30s — handles cold starts that can take 30+ seconds
    retryDelay: (attempt) => Math.min(2000 * Math.pow(2, attempt), 30000),
  });

  return {
    token: query.data,
    // isFetching stays true during all retry attempts, unlike isLoading which only covers the initial fetch
    isLoading: query.isFetching,
    error: query.isError && !query.isFetching ? query.error : null,
    refetch: query.refetch,
  };
};
